import { Table } from 'antd';
import PropTypes from 'prop-types';
import { memo, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { TableShimmer } from 'components/custom-table/TableShimmer';
import { Container } from 'components/custom-table/Style';

const StyledTable = styled(Table)`
	table {
		text-align: left;
		thead {
			tr {
				&:first-child {
					th:first-child {
						border-top-left-radius: 4px;
					}
					th:last-child {
						border-top-right-radius: 4px;
					}
				}

				th.ant-table-cell {
					font-size: 14px;
					color: ${(props) => props.theme.colors.white};
					background-color: ${(props) => props.theme.colors.TableColor};
					text-align: left;
					word-break: keep-all;
					word-wrap: break-word;
					padding: 15px;
					font-weight: 400;
				}
			}

			.ant-table-selection-column {
				.ant-checkbox-indeterminate {
					.ant-checkbox-inner {
						&::after {
							color: ${(props) => props.theme.colors.Ming};
						}
					}
				}
			}

			.ant-checkbox-indeterminate .ant-checkbox-inner::after {
				background-color: ${(props) => props.theme.colors.primary};
			}
		}
	}

	.ant-table-container {
		overflow: auto;
	}

	&.hide {
		display: none;
	}

	&.show {
		display: block;
	}
`;

function InfiniteScrollTable(props) {
	const { data, setData, dispatchAction, columns, rowKey, rowClassName, isLoading } = props;
	const dispatch = useDispatch();
	const [isInitialLoading, setIsInitialLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [offset, setOffset] = useState(0);
	const [dataReceivedFromWebsocket, setDataReceivedFromWebsocket] = useState(false);
	const limit = 1000;

	useEffect(() => {
		if (Array.isArray(data)) {
			setDataReceivedFromWebsocket(true);
		}
	}, [data]);

	useEffect(() => {
		if (!dispatchAction || dataReceivedFromWebsocket || data.length > 0) return;

		setIsInitialLoading(true);
		dispatch(
			dispatchAction({
				search_type: 'temperature_tracking',
				offset: 0,
				limit,
			})
		)
			.then((res) => {
				if (res && res.rows) {
					setData(res.rows || []);
					setHasMore(res.rows.length >= limit);
					setOffset(limit);
				}
			})
			.finally(() => {
				setIsInitialLoading(false);
			});
	}, [dispatchAction, setData, data.length, dataReceivedFromWebsocket]);

	useEffect(() => {
		setOffset(limit);
		setHasMore(data.length >= limit);
	}, [data, limit]);

	const fetchMoreData = () => {
		if (loadingMore || dataReceivedFromWebsocket) return;

		setLoadingMore(true);
		dispatch(
			dispatchAction({
				search_type: 'temperature_tracking',
				offset,
				limit,
			})
		)
			.then((res) => {
				if (res && res.rows) {
					setData((prevData) => {
						const existingTripIds = new Set(prevData.map((item) => item.trip_id));
						const newRows = res.rows.filter(
							(item) => !existingTripIds.has(item.trip_id)
						);
						return [...prevData, ...newRows];
					});
					setHasMore(res.rows.length >= limit);
					setOffset((prev) => prev + limit);
				} else {
					setHasMore(false);
				}
			})
			.finally(() => {
				setLoadingMore(false);
			});
	};

	// Use either external isLoading prop or internal isInitialLoading state
	const showLoading = isLoading || (isInitialLoading && data.length === 0);

	return (
		<Container>
			<TableShimmer rowsCount={limit} columns={columns} loading={showLoading} />

			<div id="scrollableDiv" style={{ overflow: 'auto' }}>
				<InfiniteScroll
					dataLength={data.length}
					next={fetchMoreData}
					hasMore={hasMore && !dataReceivedFromWebsocket}
					loader={
						<div style={{ textAlign: 'center', padding: '20px' }}>
							Loading more data...
						</div>
					}
					endMessage={
						<div style={{ textAlign: 'center', padding: '20px' }}>
							{data.length > 0 && 'You have seen all data'}
						</div>
					}
					scrollableTarget="scrollableDiv"
				>
					<StyledTable
						rowKey={rowKey}
						columns={columns}
						dataSource={data}
						pagination={false}
						scroll={{ x: 'max-content' }}
						className={showLoading ? 'hide' : 'show'}
						rowClassName={rowClassName}
					/>
				</InfiniteScroll>
			</div>
		</Container>
	);
}

export default memo(InfiniteScrollTable);

InfiniteScrollTable.propTypes = {
	data: PropTypes.array.isRequired,
	setData: PropTypes.func.isRequired,
	dispatchAction: PropTypes.func.isRequired,
	columns: PropTypes.array.isRequired,
	rowKey: PropTypes.string.isRequired,
	rowClassName: PropTypes.func,
	isLoading: PropTypes.bool,
};

InfiniteScrollTable.defaultProps = {
	isLoading: false,
};
